import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { css } from "@emotion/css";
import { Button, Checkbox, Icon, Menu, Pagination, Table, Confirm, Select, Input } from "semantic-ui-react";
import { convertToMinFraction, dynamicSortMultiple, convertCamelCase, convertToDateString } from "../../../Helpers";
import { GlobalConstants, TableTypesDictionary } from "../../constants/GlobalConstants";
import LmsMessage from "./LmsMessage";
import LMSScreenActionButtons from "./LmsScreenActionButtons";
import {
  useFetchData,
  localStorageSave,
  IHandleSubmitOptions,
  useForm,
  usePromiseAll,
  MethodTypes,
  isDateString,
  appendIdToPath,
  downloadFile,
  getUniqueGridIdString,
  getDataType,
  isEmptyValue,
  removeProp,
  getSortedColumns,
  createConfirmationObj,
  getParsedPropItemFromLegacy,
  getDateFormat
} from "../../../helpers/GlobalHelper";
import { useLmsGlobalFilter } from "../../../hooks/UseLmsFilterHook";
import "../../../styles/table.scss";
import "../../../styles/lms/lms-global.scss";
import "../../../styles/lms/lms-table.scss";
import "../../../styles/ui.scss";
import BCLoader from "../../ui/BCLoader";
import { BCFormController, PayloadContentType, ModalContentType } from "./LmsFormGenerator";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider } from "react-dnd";
import LmsTableColumnHeader from "./LmsTableColumnHeader";
import LmsGroupByHeaderList from "./LmsTableGroupingHeader";
import { FeatureFlags } from "../../constants/LocalServerConstants";
import { getIsWhiteLabeled } from "../../../helpers/PermissionHelper";
import BCErrorBoundary from "../../BCErrorBoundary";
import BCModal from "../../ui/BCModal";
import { ModalSizesType } from "../../interfaces/Global";
import LmsFilterSearchBar from "./LmsFilterSearchBar";
import LmsTableHeaderFilterConfigFields from "./LmsTableHeaderFilterConfigFields";
import LmsTableRow from "./LmsTableRow";
import { useBCGlobalReducer } from "../../BCGlobalStore";

type TableActionButtonNamesType = "add" | "edit" | "copy" | "remove" | "export" | "close" | "activate" | "deactivate" | "archive" | "save" | "cancel" | "addselected" | "setachtrue" | "setachfalse" | "sendemail" | "submitselected" | string;

export interface ITableActionCustomButton {
  text?: string;
  icon?: string;
  action?: TableActionButtonNamesType;
  path?: string | ((row: any) => string);
  handleClick?: any;
  isDisabled?: any;
  submitSuffix?: string;
  downloadSuffix?: string;
  payloadContentType?: PayloadContentType;
  downloadMany?: boolean;
  fileName?: string;
  method?: "PUT" | "POST";
  data?: any;
  onSuccessFunc?: (options?: any) => any,
  clearSuffixes?: string[];
  modalContent?: ModalContentType
  modalSize?: ModalSizesType
  refreshDataOnSuccess?: boolean
  onDownloadSuccessFunc?: () => any,
  handleFormatSubmitSelectedRows?: any,
  confirmationMessage?: string
  alertMessage?: string
  isIframeModal?: boolean
  height?: number
}

export type TableActionButtonTypes = React.ReactElement | ITableActionCustomButton | TableActionButtonNamesType;

type ColumnClickPropType = string;

interface IColumnClickOptions {
  path?: string | ((rowData?: any, prop?: string, i?: number) => string);
  idProp: string
  params?: string[]
  downloadPath?: string
  downloadMethod?: MethodTypes
  downloadPayloadContentType?: PayloadContentType
  downloadHasData?: boolean
  downloadFileName?: string
  isModalContentLink?: boolean
  isPopupLink?: boolean
  isIframeModal?: boolean
  isClickableConditionFunc?: (rowData?: any, prop?: string) => boolean
}

interface IDataFormatRules {
  field: string,
  rule: "format" | "hide" | "formatDate"
  display: "fullDate" | "date" | "dollar" | "full" | "datefullformatted" | "percent"
}

export interface LMSTableProps {
  data?: any;
  dataRules?: IDataFormatRules[];
  pagination?: number;
  sortable?: string[];
  fixed?: boolean;
  overflowX?: boolean;
  overflowY?: boolean;
  checkbox?: boolean;
  celled?: boolean;
  thin?: boolean;
  selectable?: boolean;
  dataSingleLine?: boolean;
  handleClick?: (rowData: any, i?: number, prop?: string) => any;
  actions?: any;
  globalActions?: any;
  handleTableInvite?: any;
  handleTableDelete?: any;
  handleTableEdit?: any;
  handleCheckbox?: any;
  handleCheckboxAll?: any;
  checkedItems?: any;
  unstackable?: boolean;
  isMobile?: boolean;
  brokerColor?: string | undefined;
  handleGMR?: any;
  handleSummary?: any;
  handleExport?: any;
  handleApplication?: any;
  handleCopyApplication?: any;
  ellipsis?: any;
  ellipsisHandlers? : any[];
  columns?: any;
  addRow?: any;
  addArray?: any [];
  handleTableAdd?: any;
  editMode?: any;
  isDeleteHide?: boolean;
  buttonWords?: any[];
  required?: boolean;
  tooltip?: any;
  multiline?: boolean;
  disableSummary?: boolean;
  disableApplication?: boolean;
  setCheckedItems?: any;
  isLoading?: boolean;
  loadingHeight?: number;
  error?: any;
  hideFilters?: boolean;
  actionButtons?: TableActionButtonTypes[];
  apiSuffix?: string;
  gridIdString?: string;
  useApiFiltering?: boolean;
  isLocal?: boolean;
  formatDataFunc?: (data: any) => any;
  skipFormat?: boolean;
  skipPagination?: boolean;
  skipFilter?: boolean;
  title?: string;
  reducerInstance?: [any, (any) => any];
  metadataSuffix?: string;
  onClickPath?: string | ((rowData?: any, prop?: string, i?: number) => string);
  onClickPathParams?: string[];
  onClickDownloadPath?: string;
  onClickDownloadMethod?: MethodTypes;
  onClickDownloadPayloadContentType?: PayloadContentType;
  onClickDownloadHasData?: boolean;
  onClickDownloadFileName?: string;
  onClickDownloadFileNameProp?: string;
  onClickIdProp?: string;
  onClickIdFunc?: (row: any, prop?: string) => string;
  isPopupLink?: boolean;
  columnClickOptions?: Record<ColumnClickPropType, IColumnClickOptions>;
  onClickModalContent?: (setIsModalOpen: any, tableInstance?: any) => any;
  onClickModalSize?: ModalSizesType;
  skipClick?: boolean;
  useMock?: boolean;
  hideProps?: string[];
  displayProps?: string[];
  displayOnlyProps?: string[];
  deleteSuffix?: string;
  archiveSuffix?: string;
  activateSuffix?: string;
  deActivateSuffix?: string;
  setACHTrueSuffix?: string;
  setACHFalseSuffix?: string;
  sendEmailSuffix?: string;
  calculatedColumns?: string[];
  myForm?: BCFormController;
  useFormInstance?: any;
  saveSuffix?: string | undefined;
  saveMethod?: MethodTypes;
  saveFormattingFunction?: (recordToUpdate: any[], inputs: any, options?: any) => any[];
  onSaveSuccessFunction?: (updatedFields?: any) => any;
  submitSelectedSuffix?: string;
  addSelectedSuffix?: string | undefined;
  handleFormatSelectedData?: (selectedData: any) => any
  selectedIdProp?: string
  clearSuffixesOnSuccess?: string[] | undefined;
  skipCheckbox?: boolean;
  isDisabled?: boolean;
  isDisableClickable?: boolean;
  groupBy?: string[] | any[];
  columnLabelsDictionary?: any;
  useGlobalDictionary?: boolean;
  isDebug?: boolean;
  skipCheckboxAll?: boolean;
  rowBackgroundDefaultColor?: string;
  rowBackgroundColor?: string;
  rowBackgroundColorConditionFunc?: (arg0: any) => boolean;
  rowsBackgroundCustomColor?: (rowData: any) => string;
  currencyFields?: string[];
  skipGrouping?: boolean;
  hideFilterBar?: boolean;
  includeFilterBar?: boolean;
  editIndividualRecordSuffix?: string;
  sortByArray?: string[];
  compact?: boolean;
  rowTitle?: string;
  isPaginationExpanded?: boolean;
  isDefaultPagination?: boolean;
  skipCache?: boolean;
  refreshDataDependency?: any;
}

interface IUseTableOptions {
  initSortedHeaders?: string[]
  tableProps?: LMSTableProps
  refreshTableData?: () => any
}

const getEditableFields = (myForm) => {
  const editableFields = {} as any;
  myForm?.fields?.map(field => {
    if (field.groupFields) {
      field.groupFields.map(gf => {
        if (gf.tableName || gf.inlineEdit) {
          const name = gf.tableName || gf.name;
          editableFields[name] = { ...gf };
        }
      });
    }
    else {
      if (field.tableName || field.inlineEdit) {
        const name = field.tableName || field.name;
        editableFields[name] = { ...field };
      }
    }
  });

  return editableFields;
};

const trueFalseDict = { true: "Yes", false: "No", Yes: true, No: false };

export const useLMSTable = (tableData = [] as any[], options = {} as IUseTableOptions ) => {
  const bcGlobalDispatch = useBCGlobalReducer()[1];
  const { initSortedHeaders = [] as any[] , tableProps, refreshTableData } = options;
  const tableSuffix = tableProps?.apiSuffix;
  const gridUniqueIdString = useMemo(() => {
    if (tableProps?.gridIdString || tableProps?.apiSuffix) {
      const uniqueId = getUniqueGridIdString(tableProps?.gridIdString || tableProps?.apiSuffix || "");
      if (FeatureFlags.IS_LMSv2_DEBUG) console.log(uniqueId);
      return uniqueId;
    }
    else {
      return "";
    }
  }, [tableProps?.apiSuffix]);

  const {
    deleteSuffix = tableSuffix,
    archiveSuffix,
    activateSuffix,
    deActivateSuffix,
    setACHTrueSuffix,
    setACHFalseSuffix,
    addSelectedSuffix,
    handleFormatSelectedData,
    clearSuffixesOnSuccess = [] as string[],
    editIndividualRecordSuffix,
    sendEmailSuffix,
    submitSelectedSuffix,
    saveSuffix,
    saveMethod = "PATCH" as MethodTypes,
    saveFormattingFunction,
    onSaveSuccessFunction,
    myForm,
    useFormInstance: tableUseFormInstance,
    groupBy,
    isDebug,
    selectedIdProp = "id",
    onClickModalContent,
    useApiFiltering = false,
    currencyFields: tableCurrencyFields = [] as any[],
    dataRules: tableDataRules = [] as any[],
    onClickModalSize
  } = tableProps as LMSTableProps;
  const [sortedData, setSortedData] = useState(tableData as any[]);
  const [sortedHeaders, setSortedHeaders] = useState(initSortedHeaders as any[]);
  const [groupByItems, setGroupByItems] = useState(groupBy as any[] | undefined);
  const [tableHeight, setTableHeight] = useState(undefined as any);
  const [activeColumn, setActiveColumn] = useState({ index: undefined, ref: undefined } as any);
  const [activeDragColumn, setActiveDragColumn] = useState({ item: "", prop: "", index: undefined } as any);
  const [checkedItems, setCheckedItems] = useState([] as any[]);
  const [apiCheckedIds, setApiCheckedIds] = useState([] as any[]);
  const absoluteInitialPositions = { isVisible: false, posX: "", posY: "" };
  const [contextMenuDisplay, setContextMenuDisplay] = useState(absoluteInitialPositions);
  const columnFilterInitialState = { isVisible: false, posX: "", posY: "", prop: "", filter: null, fieldType: "" } as any;
  const [columnFilter, setColumnFilter] = useState(columnFilterInitialState);
  const [filtersSubmitModal, setFiltersSubmitModal] = useState(absoluteInitialPositions);
  const history = useHistory();
  const tableFilterReducerInstance = useLmsGlobalFilter(sortedData, { storageId: tableProps?.gridIdString || tableSuffix, useApiFiltering, gridUniqueIdString });
  const [{
    filteredData,
    searchValue,
    pagination,
    setPagination,
    activePage,
    setActivePage,
    columns,
    setColumns,
    columnsList,
    setColumnsList,
    currentViewColumns,
    currentViewGroups,
    currentViewSort,
    currentViewSettings,
    setCurrentViewGroups,
    firstEntryIndex,
    setFirstEntryIndex,
    lastEntryIndex,
    setLastEntryIndex,
    totalGridCount,
    setTotalGridCount,
    filterPageData
  }, tableReducerDispatch]: any = tableFilterReducerInstance;
  const [{ isLoading: submitting, setLoading, fetchData }] = useFetchData();
  const [activeButtonText, setActiveButtonText] = useState("");
  const [info, setInfo] = useState({ isVisible: false } as Record<string, any>);
  const [isEditable, setIsEditable] = useState(false);
  const { validations = {}, initIsDisabled, calculatedFields, recordId, clearSuffixesOnSubmit, hiddenFieldsRules = {}, allDropdownOptions } = myForm || {} as any;
  const useFormInstance = tableUseFormInstance || useForm({}, { validations, initIsDisabled, calculatedFields, recordId, clearSuffixesOnSubmit, hiddenFieldsRules, allDropdownOptions });
  const { inputs = {} as any, setErrors, validateForm, resetForm, globalCacheDispatch } = useFormInstance as any;
  const { promisesData: allDropdownOptionsData, promisesLoading: allDropdownOptionsLoading } = usePromiseAll(allDropdownOptions, { isReady: isEditable });
  const [confirmOptions, setConfirmOptions] = useState({} as any);
  const [isOnClickModalOpen, setIsOnClickModalOpen] = useState(false);
  const [modalIframeSrcPath, setModalIframeSrcPath] = useState("");
  const iframeModalRef = useRef(null);
  const [clickedRowData, setClickedRowData] = useState({} as any);

  useEffect(() => {
    setSortedData(tableData);
  }, []);

  useEffect(() => {
    if (!useApiFiltering) {
      setCheckedItems([]);
    }
  }, [filteredData]);

  useEffect(() => {
    if (!useApiFiltering) {
      setCheckedItems([]);
    }
  }, [currentViewSort]);

  const handlePageChange = (activePage: any, pagination: any) => {
    if ((activePage > 0 && pagination && activePage <= Math.ceil(totalGridCount / pagination)) || activePage === "" || activePage === undefined) {
      setActivePage(activePage);
    }
  };

  const handleHeaderClick = (header: string) => {
    sortedData.sort(dynamicSortMultiple(header));
    const index = sortedHeaders.indexOf(header);
    if (index !== -1) {
      sortedHeaders.splice(index, 1);
      sortedData.reverse();
    }
    else {
      sortedHeaders.push(header);
    }
    
    setSortedData([...sortedData]);
    setSortedHeaders([...sortedHeaders]);
  };

  const handleHide = (prop: string, dataRules:any[] = [], hideProps: string[] = [], displayProps: string[] = [], displayOnlyProps?: any[]) => {
    let show = true;

    if (displayOnlyProps && !displayOnlyProps.includes(prop)) {
      return false;
    }

    const item = convertCamelCase(prop);
    if (dataRules && Array.from(dataRules, x => x.field).includes(prop)) {
      const dataRule = dataRules.find(x => x.field === prop);
      if (dataRule.rule === "hide") {
        show = false;
      }
    }

    if ((
      prop.includes("_") ||
      item.endsWith("Id") ||
      prop.toLowerCase() === "id" ||
      item.endsWith("Csvid") ||
      prop === "lockVersion" ||
      hideProps.includes(prop || item)) && !displayProps.includes(prop || item) ||
      prop.startsWith("idOf")
    ) {
      show = false;
    }

    return show;
  };

  const handleCheckboxAll = (data) => {
    if (filteredData && filteredData.length > 0) {
      if (data?.checked) {
        const filteredIndexes = filteredData.map((_,i) => i);
        setCheckedItems(filteredIndexes);
      } else {
        setCheckedItems([]);
      }
    }
  };

  const setAlertMessage = (alertMessage, funcToRun = undefined as any) => {
    if (alertMessage) {
      setConfirmOptions(createConfirmationObj(funcToRun, alertMessage, true));
    }
  };

  const isAmountField = (item, prop) => {
    if (Number(item) === item) {
      const amountFieldEndings = ["amount", "value", "payment", "income", "reserve", "balance", "price", "piece", "unpaid", "paid", "fee", "wireamt", "drawamt", "disbursed"];
      const propLower = prop?.toLowerCase() || "";
      return amountFieldEndings.some(endingString => {
        return propLower.includes(endingString) && !propLower.includes("percent") && !propLower.includes("number");
      });
    }
    else {
      return false;
    }
  };

  const getPercentCurrencyField = (item: any, prop: string, dataRules = tableDataRules, currencyFields = tableCurrencyFields) => {
    if ((currencyFields && currencyFields.includes(prop))) {
      return "currency";
    }
    else if (isAmountField(item, prop)) {
      return "currency";
    }
    else if (prop?.toLowerCase()?.includes("percent") && !prop?.toLowerCase().includes("exclude")) {
      return "percentage";
    }
    else if (dataRules?.length > 0 && Array.from(dataRules, x => x.field).includes(prop)) {
      const dataRule = dataRules.find(x => x.field === prop);
      if (item && dataRule?.rule === "format" && dataRule?.display === "dollar") {
        return "currency";
      }
      else if (item && dataRule?.rule === "format" && dataRule?.display === "percent") {
        return "percentage";
      }
    }

    return "number";
  };

  const formatPercentage = (item) => {
    const percentValue = !isEmptyValue(item) && item < 1 ? item * 100 : item;
    const formattedPercentValue = !isEmptyValue(percentValue) ? convertToMinFraction(percentValue, 2, 2, true) : undefined;
    return formattedPercentValue ? `${formattedPercentValue} %` : "";
  };

  const handleFormat = (item: any, prop: string, dataRules?: any[], currencyFields?: any[], typeOptions = {} as any) => {
    if (typeOptions?.numberFormat === "currency" || (currencyFields && currencyFields.includes(prop)) || isAmountField(item, prop)) {
      if (!isEmptyValue(item)) {
        return "$" + convertToMinFraction(item, 2, 2, true);
      }

      return "";
    }

    if (typeOptions?.numberFormat === "percentage" || prop?.toLowerCase()?.includes("percent") && !prop?.toLowerCase()?.includes("exclude")) {
      return formatPercentage(item);
    }

    if (dataRules && Array.from(dataRules, x => x.field).includes(prop)) {
      const dataRule = dataRules.find(x => x.field === prop);
      if (item && dataRule?.rule === "format" && dataRule?.display === "dollar") {
        return "$" + convertToMinFraction(item);
      }

      if (item && dataRule?.rule === "format" && dataRule?.display === "percent") {
        return formatPercentage(item);
      }

      if (item && dataRule?.rule === "format" && dataRule?.display === "fullDate") {
        return convertToDateString(item, "datetimewithoutsecond");
      }

      if (item && dataRule?.rule === "format" && dataRule?.display === "date") {
        return convertToDateString(item, "date");
      }

      if (item && dataRule?.rule === "formatDate") {
        return convertToDateString(item, dataRule?.display);
      }
    }

    if (typeof item === "string" && item.includes("T") && isDateString(item)) {
      if (prop?.toLowerCase().endsWith("on")) {
        return convertToDateString(item, "datetimewithoutsecond");
      }
      else {
        return convertToDateString(item, "date");
      }
    }

    if (typeof item === "boolean" || item === "true" || item === "false") {
      const booleanDictionary = {
        true: "Yes",
        false: "No"
      };

      return booleanDictionary[item];
    } else {
      if (typeof item !== "string" && typeof item !== "number") {
        return "";
      }
    }

    return item;
  };

  const handleCheckbox = (index, checked, rowData) => {
    let updatedCheckedItems;
    if (!checkedItems.includes(index)) {
      updatedCheckedItems = [...checkedItems, index];
    }
    else {
      updatedCheckedItems = checkedItems.filter(ci => ci !== index);
    }

    if (useApiFiltering) {
      const updatedApiCheckedIds = [...apiCheckedIds];
      if (checked && rowData?.[selectedIdProp] && !apiCheckedIds.includes(rowData?.[selectedIdProp])) {
        updatedApiCheckedIds.push(rowData[selectedIdProp]);
      }
      else {
        const index = updatedApiCheckedIds.indexOf(rowData[selectedIdProp]);
        if (index > -1) {
          updatedApiCheckedIds.splice(index, 1);
        }
      }

      setApiCheckedIds(updatedApiCheckedIds);
    }

    setCheckedItems(updatedCheckedItems);
  };

  const handlePush = (path) => {
    history.push(`${path}`);
  };

  const handlePushBack = useCallback(
    () => {
      history.goBack();
    },
    [history]
  );

  const handleActionClick = async (options) => {
    if (options.isClickable || options.isClickable === undefined) {
      if (options.downloadPath) {
        const response = await downloadFile(
          options.downloadPath,
          {
            fileName: options.fileName,
            id: options?.id,
            method: options.downloadMethod,
            data: options?.data,
            contentType: options.downloadPayloadContentType
          }
        );

        if (response) {
          setInfo({ isVisible: true, ...response });
        }
      }
      else {
        if (options.isModalContentLink || (onClickModalContent && options.isRowClick)) {
          setClickedRowData(options);
          setIsOnClickModalOpen(true);
        }
        else {
          if (tableSuffix) {
            localStorageSave("prevtable", tableSuffix, "session");
          }

          const parsedPath = appendIdToPath(options);
          const fullUrl = parsedPath ? `${window.location.origin}${parsedPath}` : "";
          if (options?.isPopupLink) {
            const popupWindow = window.open(fullUrl, "popupWindow", "width=1200,height=800,left=100,right=100,resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,directories=no,status=no");
            popupWindow?.sessionStorage?.setItem("popupTitle", parsedPath);
          }
          else if (options?.isIframeModal) {
            bcGlobalDispatch({
              type: GlobalConstants.BCStoreReducerCases.SET_IFRAME_OPTIONS,
              payload: { modalIframeSrcPath: fullUrl, iframeModalSize: options?.modalSize || onClickModalSize || "fullscreen" }
            });
          }
          else {
            handlePush(appendIdToPath(options));
          }
        }
      }
    }
  };

  const handleSubmit = (submitSuffix, { method = "DELETE", data = null, buttonText = "", onSuccessFunc, refreshDataOnSuccess, alertMessage, payloadDataType, clearSuffixes = [] }: IHandleSubmitOptions) => {
    if (alertMessage) {
      setTimeout(() => {
        alert(alertMessage);
      },50);
    }
    else {
      setLoading(true);
      setActiveButtonText(buttonText);
      fetchData(submitSuffix, { fetchMethod: method, data, payloadDataType }).then(response => {
        if (!response.error) {
          setInfo({
            header: GlobalConstants.DefaultValues.messageHeaderSuccessText,
            message: GlobalConstants.DefaultValues.messageTableSubmitSuccess,
            type: GlobalConstants.ErrorLevels.INFO,
            isVisible: true
          });

          const suffixesToClear = [tableSuffix, ...clearSuffixesOnSuccess, ...clearSuffixes];
          globalCacheDispatch({
            type: GlobalConstants.BCStoreReducerCases.CLEAR_API_CACHE,
            payload: { apiSuffixes: suffixesToClear }
          });

          if (onSuccessFunc && typeof onSuccessFunc === "function") {
            onSuccessFunc();
          }

          if (refreshDataOnSuccess && refreshTableData) {
            refreshTableData();

            if (useApiFiltering && apiCheckedIds.length > 0) {
              setApiCheckedIds([]);
              setCheckedItems([]);
              setActivePage(1);
            }
          }
        }
        else {
          setInfo({
            header: response?.error?.errorTitle || GlobalConstants.DefaultValues.messageHeaderText,
            message: response?.error?.message,
            errorResponse: response?.error?.errorResponse,
            type: GlobalConstants.ErrorLevels.ERROR,
            isVisible: true
          });
        }
      }).catch(e => {
        setInfo({
          header: GlobalConstants.DefaultValues.messageHeaderText,
          message: e?.message,
          type: GlobalConstants.ErrorLevels.ERROR,
          isVisible: true
        });

        if (FeatureFlags.IS_DEBUG) {
          console.log(e);
        }
      }).finally(() => {
        setLoading(false);
      });
    }
  };

  const getCheckedIds = (index = "" as string | number) => {
    if (useApiFiltering) return apiCheckedIds;
    const checkedIds = [] as any[];

    if (index) {
      checkedIds.push(index);
    }
    else {
      checkedItems?.map(item => {
        if (filteredData?.[item]?.[selectedIdProp]) {
          checkedIds.push(filteredData[item][selectedIdProp]);
        }
      });
    }

    return checkedIds;
  };

  const checkedIds = useMemo(() => {
    return getCheckedIds();
  }, [checkedItems, apiCheckedIds]);

  const getCheckedRowsData = (index = "" as string | number) => {
    const checkedRowsData = [] as any[];

    if (index) {
      checkedRowsData.push(filteredData[index]);
    }
    else {
      checkedItems?.map(item => {
        checkedRowsData.push(filteredData[item]);
      });
    }

    return checkedRowsData;
  };

  const handleDelete = (index, data = filteredData) => {
    if (data && deleteSuffix) {
      setConfirmOptions(createConfirmationObj(() => {
        handleSubmit(deleteSuffix, { method: "DELETE", buttonText: "Delete", data: checkedIds, refreshDataOnSuccess: true });
      }, `Are you sure you want to delete the selected record${checkedItems?.length > 1 ? "s" : ""}?`));
    }
  };

  const handleTrueFalseSubmit = (suffix, buttonText = "" as string) => {
    if (suffix) {
      const updateDataOnSuccess = () => {
        if ((tableSuffix || editIndividualRecordSuffix) && checkedIds && checkedIds.length > 0) {
          const singleRecordSuffix = editIndividualRecordSuffix || tableSuffix?.substring(0, tableSuffix?.length - 1);
          const suffixesToClearOnSuccess = checkedIds?.map(chId => {
            return `${singleRecordSuffix}/${chId}`;
          });

          globalCacheDispatch({
            type: GlobalConstants.BCStoreReducerCases.CLEAR_API_CACHE,
            payload: { apiSuffixes: suffixesToClearOnSuccess }
          });
        }
      };

      handleSubmit(suffix, { method: "PUT", buttonText, data: checkedIds, onSuccessFunc: updateDataOnSuccess, refreshDataOnSuccess: true });
    }
  };

  const handleArchive = () => {
    setConfirmOptions(createConfirmationObj(() => {
      handleTrueFalseSubmit(archiveSuffix, "Archive");
    }, `Are you sure you want to archive the selected record${checkedItems?.length > 1 ? "s" : ""}?`));
  };

  const handleActivate = () => {
    setConfirmOptions(createConfirmationObj(() => {
      handleTrueFalseSubmit(activateSuffix, "Activate");
    }, `Are you sure you want to activate the selected record${checkedItems?.length > 1 ? "s" : ""}?`));
  };

  const handleDeActivate = () => {
    setConfirmOptions(createConfirmationObj(() => {
      handleTrueFalseSubmit(deActivateSuffix, "De-Activate");
    }, `Are you sure you want to de-activate the selected record${checkedItems?.length > 1 ? "s" : ""}?`));
  };

  const handleSetACHTrue = () => {
    setConfirmOptions(createConfirmationObj(() => {
      handleTrueFalseSubmit(setACHTrueSuffix, "Set ACH True");
    }, `Are you sure you want to Set ACH True the selected record${checkedItems?.length > 1 ? "s" : ""}?`));
  };

  const handleSetACHFalse = () => {
    setConfirmOptions(createConfirmationObj(() => {
      handleTrueFalseSubmit(setACHFalseSuffix, "Set ACH False");
    }, `Are you sure you want to Set ACH False the selected record${checkedItems?.length > 1 ? "s" : ""}?`));
  };

  const handleSendEmail = () => {
    handleSubmit(sendEmailSuffix, { method: "POST", data: getCheckedIds(), buttonText: "Send Email" });
  };

  const handleSubmitSelected = (
    {
      submitSuffix = submitSelectedSuffix,
      method = "POST" as MethodTypes,
      data = checkedIds as ((checkedIds: any[]) => any) | any[],
      buttonText = "Add Selected",
      onSuccessFunc = undefined as any,
      payloadDataType = "JSON" as PayloadContentType,
      refreshDataOnSuccess = false,
      clearSuffixes = [] as string[],
      handleFormatSubmitSelectedRows = undefined as any,
      confirmationMessage = undefined as string | undefined,
      alertMessage = undefined as string | undefined
    }
  ) => {
    if (typeof data === "function") {
      try {
        data = data(checkedIds);
      } catch (e) {
        if (FeatureFlags.IS_DEBUG) {
          console.log(e);
        }
      }
    }
    else if (typeof handleFormatSubmitSelectedRows === "function") {
      data = handleFormatSubmitSelectedRows(getCheckedRowsData());
    }

    const onSuccess = () => {
      if (onSuccessFunc) {
        onSuccessFunc({ checkedIds: checkedIds, checkedRowsData: getCheckedRowsData(), filteredData });
      }
    };

    const submitSelected = () => handleSubmit(submitSuffix, { method, data, buttonText, onSuccessFunc: onSuccess, refreshDataOnSuccess, alertMessage, payloadDataType, clearSuffixes });
    if (confirmationMessage) {
      setConfirmOptions(createConfirmationObj(submitSelected, confirmationMessage));
    }
    else {
      submitSelected();
    }
  };

  const handleAddSelected = (data = getCheckedIds(), customAddSelectedSuffix = "", method = GlobalConstants.FetchMethods.POST as MethodTypes, buttonText = "Add Selected") => {
    if (customAddSelectedSuffix || addSelectedSuffix) {
      const selectedData = handleFormatSelectedData ? handleFormatSelectedData(getCheckedRowsData()) : data;
      handleSubmit(customAddSelectedSuffix || addSelectedSuffix, {
        method: method,
        buttonText: buttonText,
        data: selectedData,
        onSuccessFunc: () => {
          if (clearSuffixesOnSuccess) {
            globalCacheDispatch({
              type: GlobalConstants.BCStoreReducerCases.CLEAR_API_CACHE,
              payload: { apiSuffixes: clearSuffixesOnSuccess }
            });
          }

          handlePushBack();
        }
      });
    }
    else {
      if (FeatureFlags.IS_DEBUG) {
        console.log("AddSelected suffix is missing.");
      }
    }
  };

  const handleDownloadSelected = async (downloadSuffix, options = {} as any) => {
    setLoading(true);
    setActiveButtonText(options.buttonText);
    const response = await downloadFile(downloadSuffix, options);
    if (response?.errorResponse) {
      setInfo({
        errorResponse: response?.errorResponse,
        type: GlobalConstants.ErrorLevels.ERROR,
        isVisible: true
      });
    }

    setLoading(false);
    setActiveButtonText("");
  };

  const handleExport = async (ab, fileType) => {
    if (ab.handleExport) {
      ab.handleExport(
        fileType,
        tableData,
        checkedItems
      );
    }
    else {
      setLoading(true);
      setActiveButtonText("Export");
      const rowsData = getCheckedRowsData();
      const fileName = "Export";

      const fields = columnsList.reduce((fieldsTemp, column) => {
        if (!column.hidden) {
          fieldsTemp.push({
            display: column.item,
            fieldType: column.numberFormat ? column.numberFormat : column.fieldType,
            name: column.field
          });
        }

        return fieldsTemp;
      }, []);

      const exportPayload = {
        data: rowsData,
        endPoint: "",
        fileName,
        fileType: fileType,
        fields
      };

      const response = await downloadFile("GridViews/export", {
        fileName,
        contentType: "JSON",
        method: "POST",
        data: exportPayload
      });

      if (response?.errorResponse) {
        setInfo({
          errorResponse: response?.errorResponse,
          type: GlobalConstants.ErrorLevels.ERROR,
          isVisible: true
        });

        setLoading(false);
      }
      else {
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    resetForm();
    setErrors();
    setIsEditable(false);
  };

  const handleSave = () => {
    const recordsToUpdate = [] as any;
    const tableErrors = {};
    const allTableErrors = [] as any[];
    Object.entries(inputs).map(([editProp, values]) => {
      const id = editProp.split("_")[0];
      if (typeof values === "object") {
        const inlineFormIsInvalid = validateForm(id, true);
        if (inlineFormIsInvalid) {
          const recordErrors = inlineFormIsInvalid[id] || inlineFormIsInvalid;
          tableErrors[id] = recordErrors;
          Object.values(recordErrors).map((fe: any) => {
            const messageToPush = ` - ${fe.fullMessage}`;
            if (fe.fullMessage && !allTableErrors.includes(messageToPush)) {
              allTableErrors.push(messageToPush);
            }
          });
        } else {
          recordsToUpdate.push({ id, ...values });
        }
      }
    });

    if (Object.keys(tableErrors).length > 0) {
      setErrors(tableErrors);
      setInfo({
        header: "Oops, there are items that require your attention.",
        allTableErrors,
        type: GlobalConstants.ErrorLevels.ERROR,
        isVisible: true
      });

      if (FeatureFlags.IS_DEBUG) {
        console.log({ recordsToUpdate, tableErrors, allTableErrors });
      }
    }
    else {
      if (saveSuffix && recordsToUpdate.length > 0) {
        const updatedRowsData = saveFormattingFunction ? saveFormattingFunction(inputs, { filteredData, recordsToUpdate }) : recordsToUpdate;
        handleSubmit(saveSuffix, {
          method: saveMethod, buttonText: "Save", data: updatedRowsData, onSuccessFunc: () => {
            if (onSaveSuccessFunction) {
              onSaveSuccessFunction(updatedRowsData);
            }

            handleCancel();
            if (refreshTableData) {
              refreshTableData();
            }
          }
        });
      }
    }
  };

  const getEditableValue = (prop, rowIndex, inputs, initialValue) => {
    if (inputs && rowIndex !== undefined && prop !== undefined && inputs[rowIndex] !== undefined && inputs[rowIndex][prop] !== undefined) {
      return { value: trueFalseDict[inputs[rowIndex][prop]] || inputs[rowIndex][prop], isEdited: true };
    }
    else {
      return { value: trueFalseDict[initialValue] || initialValue };
    }
  };

  const clearColumnFilter = () => {
    setColumnFilter(columnFilterInitialState);
  };

  if (isDebug && FeatureFlags.IS_DEBUG) {
     console.log({ filteredData, columns, checkedItems });
  }

  return {
    sortedData,
    sortedHeaders,
    setSortedHeaders,
    columns,
    setColumns,
    columnsList,
    setColumnsList,
    currentViewColumns,
    currentViewGroups,
    currentViewSettings,
    setCurrentViewGroups,
    checkedItems,
    checkedIds,
    apiCheckedIds,
    setApiCheckedIds,
    firstEntryIndex,
    lastEntryIndex,
    totalGridCount,
    setSortedData,
    activePage,
    setActivePage,
    setFirstEntryIndex,
    setLastEntryIndex,
    pagination,
    setPagination,
    setTotalGridCount,
    setCheckedItems,
    handlePageChange,
    handleHeaderClick,
    handleHide,
    handleFormat,
    getPercentCurrencyField,
    handleCheckbox,
    handleActionClick,
    handlePush,
    tableFilterReducerInstance,
    tableReducerDispatch,
    filteredData,
    filterPageData,
    searchValue,
    handleDelete,
    handleExport,
    handlePushBack,
    activeButtonText,
    submitting,
    handleArchive,
    handleActivate,
    handleDeActivate,
    handleAddSelected,
    handleSubmitSelected,
    handleDownloadSelected,
    handleSetACHTrue,
    handleSetACHFalse,
    handleSendEmail,
    handleSave,
    handleCancel,
    info,
    setInfo,
    isEditable,
    setIsEditable,
    getEditableValue,
    useFormInstance,
    inputs,
    allDropdownOptionsData,
    allDropdownOptionsLoading,
    getCheckedIds,
    getCheckedRowsData,
    tableHeight,
    setTableHeight,
    activeColumn,
    setActiveColumn,
    activeDragColumn,
    setActiveDragColumn,
    groupByItems,
    setGroupByItems,
    contextMenuDisplay,
    setContextMenuDisplay,
    columnFilter,
    setColumnFilter,
    filtersSubmitModal,
    setFiltersSubmitModal,
    clearColumnFilter,
    handleCheckboxAll,
    confirmOptions,
    setConfirmOptions,
    setAlertMessage,
    isOnClickModalOpen,
    setIsOnClickModalOpen,
    modalIframeSrcPath,
    setModalIframeSrcPath,
    iframeModalRef,
    clickedRowData,
    setClickedRowData,
    firstCheckedRow: checkedItems?.[0] !== undefined && filteredData ? filteredData[checkedItems[0]] : {},
    refreshTableData,
    gridUniqueIdString
  };
};

const HideColumnsContextMenu = (
  {
    isVisible = false,
    posX = "0",
    posY = "0",
    columnsList = [] as any[],
    setColumnsList,
    setContextMenuDisplay,
    tableFilterReducerInstance
  }
) => {
  const contextMenuRef = useRef(null as any);
  const tableReducerDispatch = tableFilterReducerInstance[1];
  const sortedColumnsList = columnsList?.sort((a, b) => a.item?.localeCompare(b.item));

  useEffect(() => {
    const handleOutsideSearchClick = (e) => {
      if (contextMenuRef.current && contextMenuRef.current.contains(e.target)) {
        return;
      }
      else {
        handleClose();
      }
    };

    if (isVisible) {
      contextMenuRef?.current?.setAttribute("listener", "true");
      document.addEventListener("mousedown", handleOutsideSearchClick);
    }

    return () => {
      if (contextMenuRef?.current?.getAttribute("listener") !== "true") {
        document.removeEventListener("mousedown", handleOutsideSearchClick);
      }
    };
  }, [isVisible, contextMenuRef]);

  const handelCheckBoxChange = (e, data) => {
    const updatedColumnsList = columnsList.map(col => {
      if (col.item === data.name) {
        return data.checked ? removeProp(col, "hidden") : { ...col, hidden: true };
      }

      return col;
    });

    setColumnsList(updatedColumnsList);
    tableReducerDispatch({
      type: GlobalConstants.LmsTableReducerCases.SET_CURRENT_VIEW_COLUMNS,
      payload: updatedColumnsList?.filter(c => !c.hidden).sort((a, b) => a.index - b.index)
    });
  };

  const handleSelectAll = (e, data) => {
    setColumnsList(columnsList.map(col => {
      return { ...col, hidden: !data.checked };
    }));
  };

  const handleClose = () => {
    setContextMenuDisplay({ isVisible: false, posX: "0", posY: "0" });
  };

  return <>
    {isVisible && <div
      ref={contextMenuRef}
      className="lms-table-columns-list"
      style={{
        top: posY,
        left: posX,
        opacity: 1
      }}
    >
      <div className="columns-list-header">
        <Checkbox label="Select All" onChange={(e, data) => handleSelectAll(e, data)}/>
        <Button onClick={handleClose}>Close</Button></div>
      {sortedColumnsList.map((col, i) => {
        return <div key={`context-menu-column${i}`}>
          <Checkbox onChange={(e, data) => handelCheckBoxChange(e, data)} name={col.item} checked={!col.hidden} label={col.item}/>
        </div>;
      })}
    </div>}
  </>;
};

const LmsTable = ({ ...props }: LMSTableProps) => {
  const {
    myForm = {} as BCFormController,
    checkbox = true,
    skipCheckbox = false,
    isDisabled = false,
    skipPagination = false,
    thin = true,
    dataSingleLine = true,
    selectable = true,
    sortByArray,
    onClickIdProp = "",
    onClickIdFunc,
    rowTitle = ""
  } = props;
  const [apiCustomHeaders, setApiCustomHeaders] = useState(undefined as any);
  const propsTableData = props.data || props.error || props.isLoading ? [{ data: props.data, error: props.error, isLoading: props.isLoading || false }] : null;
  const [{ data: tableData, error = false, isLoading = false, responseHeaders = null }, refreshTableData] = propsTableData ? propsTableData : useFetchData(props.apiSuffix || "", {
    useMock: props.useMock,
    skipCache: props.skipCache || props?.useApiFiltering,
    customHeaders: apiCustomHeaders,
    isReady: !props?.useApiFiltering || !!apiCustomHeaders,
    getResponseHeadersList: ["X-Total-Count"],
    defaultData: [],
    formatDataFunc: props.formatDataFunc
  });

  const isBrokerColor = (props.brokerColor && getIsWhiteLabeled());
  const editableFields = getEditableFields(myForm);
  const initTableData = props.apiSuffix === undefined && Array.isArray(props.data) ? props.data : Array.isArray(tableData) ? tableData : [];
  const useLmsTableInstance = useLMSTable(initTableData, { refreshTableData, tableProps: props });
  const tableRef = useRef<HTMLDivElement>(null);
  const dropContainerRef = useRef<HTMLDivElement>(null);
  const {
    sortedData,
    sortedHeaders,
    checkedItems,
    activePage,
    setActivePage,
    firstEntryIndex,
    lastEntryIndex,
    pagination,
    setPagination,
    totalGridCount,
    setSortedData,
    setLastEntryIndex,
    setTotalGridCount,
    handlePageChange,
    handleHeaderClick,
    handleHide,
    handleFormat,
    getPercentCurrencyField,
    handleCheckbox,
    handleActionClick,
    handlePush,
    handleDelete,
    tableFilterReducerInstance,
    tableReducerDispatch,
    filteredData,
    filterPageData,
    info,
    setInfo,
    isEditable,
    setIsEditable,
    getEditableValue,
    useFormInstance,
    allDropdownOptionsData,
    allDropdownOptionsLoading,
    columns,
    setColumns,
    columnsList,
    setColumnsList,
    currentViewColumns,
    currentViewGroups,
    currentViewSettings,
    setCurrentViewGroups,
    setTableHeight,
    tableHeight,
    activeColumn,
    setActiveColumn,
    activeDragColumn,
    setActiveDragColumn,
    contextMenuDisplay,
    setContextMenuDisplay,
    columnFilter,
    setColumnFilter,
    clearColumnFilter,
    handleCheckboxAll,
    confirmOptions,
    setConfirmOptions,
    isOnClickModalOpen,
    setIsOnClickModalOpen,
    modalIframeSrcPath,
    setModalIframeSrcPath,
    iframeModalRef,
    gridUniqueIdString
  } = useLmsTableInstance;
  const optionsSuffix = gridUniqueIdString ? `gridviews/grid-info/${gridUniqueIdString}` : "";
  const [{
    data: gridInfo,
    isLoading: isSettingsOptionsLoading,
    error: settingsOptionsError,
    setData: setSettingOptions
  }, refreshSettingsOptions] = useFetchData(optionsSuffix, { defaultData: {} });
  const settingOptions = gridInfo?.gridViewSettings;
  const columnsTypesApi = gridInfo?.gridColumns?.map((col: any) => ({ ...col, ...(getParsedPropItemFromLegacy(col.columnName) || {}) }));
  const totalPages = Math.ceil(totalGridCount / pagination);
  const isLoaderVisible = isLoading || isSettingsOptionsLoading || (!error && !settingsOptionsError && !tableData);
  const displayTable = !isLoaderVisible && (columns?.length > 0 || tableData?.length > 0);
  const displayEmptyState = !isLoaderVisible && columns?.length === 0 && tableData?.length === 0;
  const brokerTableHeaderOverrideCSS = css`
    background: ${props.brokerColor ? props.brokerColor : "black"} !important;
  `;

  const brokerTableEllipsesOverrideCSS = css`
    color: ${props.brokerColor ? props.brokerColor : "black"} !important;
  `;
  const columnResizingStyle = activeColumn && activeColumn.index !== undefined ? " lms-table-column-active-resizing" : "";

  const activeResizeStyles = css`
    height: ${tableHeight}px;
    border-color: #1d2433;
  `;

  const { inputs = {}, errors = {}, handleChange } = useFormInstance as any;

  const yesNoDropdownOptions = [
    { key: "Yes", value: "Yes", text: "Yes" },
    { key: "No", value: "No", text: "No" },
  ];

  const getColumnsFromData = useCallback((filteredData) => {
    const headers = filteredData && filteredData[0] ? Object.keys(filteredData[0]) : [];
    return headers?.map(prop => ({ prop })) as any;
  }, [filteredData, currentViewColumns]);

  const getDataSample = useCallback((data) => {
    const dataSample = {} as any;
    if (data?.[0]) {
      Object.entries(data[0]).map(([key, value]) => {
        if (dataSample[key] === undefined) {
          if (isEmptyValue(value)) {
            data.some((rowData) => {
              if (dataSample[key] === undefined) {
                if (!isEmptyValue(rowData[key])) {
                  dataSample[key] = rowData[key];
                  return true;
                }
              }
            });
          }
          else {
            dataSample[key] = value;
          }
        }
      });
    }

    return dataSample;
  }, [tableData, currentViewColumns, settingsOptionsError]);

  const getColumnsList = (currentViewColumns, data) => {
    const finalColumnsList = [] as any[];
    const firstRowData = getDataSample(data);
    const fieldTypes = {};
    if (currentViewColumns && currentViewColumns.length > 0) {
      currentViewColumns.map(({ prop, hidden, width, index }) => {
        let parsedItem = convertCamelCase(prop);
        if (props.useGlobalDictionary && GlobalConstants.TableColumnLabelsDictionary[prop]) {
          parsedItem = GlobalConstants.TableColumnLabelsDictionary[prop];
        }
        if (props.columnLabelsDictionary && props.columnLabelsDictionary[prop]) {
          parsedItem = props.columnLabelsDictionary[prop];
        }

        if (handleHide(prop, props.dataRules, props.hideProps, props.displayProps, props.displayOnlyProps)) {
          const columnTypeFormatFromApi = columnsTypesApi?.find((column: any) => column.prop === prop)?.columnFormat;
          const columnTypeParsed = columnTypeFormatFromApi && TableTypesDictionary[columnTypeFormatFromApi] ? TableTypesDictionary[columnTypeFormatFromApi] : undefined;
          const fieldType = columnTypeParsed === "currency" || columnTypeParsed === "percentage" ? "number" : getDataType(firstRowData?.[prop]);
          fieldTypes[prop] = fieldType;
          const column: any = {
            item: parsedItem,
            field: prop,
            prop,
            fieldType,
            numberFormat: columnTypeParsed ? columnTypeParsed : fieldType === "number" ? getPercentCurrencyField(parsedItem, prop) : "",
            dateFormat: fieldType === "date" ? getDateFormat(firstRowData?.[prop]) : "",
            width,
            index
          };

          if (hidden) {
            column.hidden = true;
          }

          finalColumnsList.push(column);
        }
      });
    }

    if (sortByArray) {
      const sortBy = sortByArray;

      if (sortBy && sortBy.length > 0) {
        const sortedHeaders = [] as any[];
        sortBy.map(soProp => {
          const foundColumnHeader = finalColumnsList.find(dh => dh.prop === soProp);
          if (foundColumnHeader) {
            sortedHeaders.push(foundColumnHeader);
          }
        });

        return { finalColumnsList: sortedHeaders, fieldTypes };
      }
    }

    return { finalColumnsList, fieldTypes };
  };

  useEffect(() => {
    if (tableData) {
      const tableDataArray = Array.isArray(tableData) ? tableData : [tableData];
      setSortedData(tableDataArray);
      if (firstEntryIndex === 0 && lastEntryIndex === 0) {  
        setLastEntryIndex(pagination && !isDisabled ? pagination : tableDataArray.length);
      }

      if (props.useApiFiltering) {
        if (!isEmptyValue(responseHeaders?.["X-Total-Count"])) {
          setTotalGridCount(responseHeaders?.["X-Total-Count"]);
        }
      }
      else {
        setTotalGridCount(tableData.length);
      }
    }
  }, [tableData, settingOptions, settingsOptionsError]);

  useEffect(() => {
    if (props.useApiFiltering && !isEmptyValue(responseHeaders?.["X-Total-Count"]) && responseHeaders?.["X-Total-Count"] != totalGridCount) {
      setTotalGridCount(parseInt(responseHeaders?.["X-Total-Count"]));
    }
  }, [responseHeaders?.["X-Total-Count"]]);

  useEffect(() => {
    if (apiCustomHeaders && tableData) {
      refreshTableData();
    }
  }, [apiCustomHeaders?.filter]);

  useEffect(() => {
    if (tableFilterReducerInstance?.[0].apiFilterHeaders) {
      if (props.useApiFiltering && (settingOptions || gridInfo || settingsOptionsError)) {
        setApiCustomHeaders(tableFilterReducerInstance?.[0].apiFilterHeaders);
      }
    }
  }, [tableFilterReducerInstance?.[0].apiFilterHeaders, gridInfo, settingsOptionsError]);

  useEffect(() => {
    if ((!props.useApiFiltering || !props.isDisabled || (currentViewColumns?.length > 0 || settingOptions?.length > 0) || settingsOptionsError && tableData && (gridInfo || ((gridInfo && !settingOptions) || isDisabled)))) {
      const columnsFromData = getColumnsFromData(tableData);
      const initialColumnsList = [] as any[];
      if (tableData && tableData.length > 0) {
        if (currentViewColumns && currentViewColumns.length > 0) {
          initialColumnsList.push(...currentViewColumns);
          columnsFromData.map((column: any) => {
            const foundColumn = currentViewColumns.find((cc: any) => cc.prop === column.prop);
            if (!foundColumn) {
              initialColumnsList.push(column);
            }
          });
        }
        else {
          initialColumnsList.push(...columnsFromData);
        }
      }

      const indexedColumns = initialColumnsList.map((column: any, index: number) => ({ ...column, index }));
      const { finalColumnsList, fieldTypes } = getColumnsList(indexedColumns, tableData) as any;
      setColumnsList(finalColumnsList);
      tableReducerDispatch({
        type: GlobalConstants.LmsTableReducerCases.SET_CURRENT_VIEW_FIELD_TYPES,
        payload: fieldTypes
      });
    }
  }, [tableData, gridInfo, settingOptions, currentViewSettings]);

  useEffect(() => {
    if (props.setCheckedItems) {
      props.setCheckedItems(checkedItems);
    }
  }, [checkedItems]);

  useEffect(() => {
    if (sortedData) {
      setTimeout(() => {
        setTableHeight(tableRef?.current?.offsetHeight);
      }, 0);
    }
  }, [sortedData]);

  useEffect(() => {
    if (props.refreshDataDependency) {
      refreshTableData();
    }
  }, [props.refreshDataDependency]);

  const handleColumnHeaderDrop = (
    {
      prevIndex,
      newIndex,
      prevProp,
      newProp,
      isGroupBy = false
    }
  ) => {
    if (isGroupBy && columns[prevIndex]) {
      if (!currentViewGroups || !currentViewGroups?.some(gi => gi.prop === prevProp)) {
        if (currentViewGroups && Array.isArray(currentViewGroups)) {
          setCurrentViewGroups([...currentViewGroups, columns[prevIndex]]);
        }
        else {
          setCurrentViewGroups([columns[prevIndex]]);
        }
      }
    }
    else {
      if (prevIndex !== newIndex) {
        const prevPropIndex = columns?.findIndex(cv => cv.prop === prevProp);
        const newPropIndex = columns?.findIndex(cv => cv.prop === newProp);
        setColumns(getSortedColumns(columns, prevIndex, newIndex));
        tableReducerDispatch({
          type: GlobalConstants.LmsTableReducerCases.SET_CURRENT_VIEW_COLUMNS,
          payload: getSortedColumns(columns, prevPropIndex, newPropIndex)
        });
      }
    }
  };

  const PaginationButtons = () => {
    const menuItems = [] as any;
    if (pagination) {
      for (let i = 0; i < Math.ceil(filteredData.length / pagination); i++) {
        menuItems.push(<Menu.Item key={`pagination-button-${i}`} as='a' onClick={() => handlePageChange(i + 1, props.pagination)}>{i + 1}</Menu.Item>);
      }
    }

    return menuItems;
  };

  const calculateColumn = (prop, filteredData) => {
    let total = 0;
    filteredData.map(rowData => {
      if (rowData[prop]) {
        total += parseFloat(rowData[prop]);
      }
    });

    return `$ ${convertToMinFraction(total, 2, 2, true)}`;
  };

  const isUpward = (i) => {
    if ((filteredData?.length - firstEntryIndex) <= pagination) {
      return (filteredData?.length - firstEntryIndex) > 3 && (filteredData?.length - i) < 3;
    }
    else {
      return (pagination - (i - firstEntryIndex)) < 3;
    }
  };

  const mouseMove = (e) => {
    if (activeColumn.index !== undefined && activeColumn.ref) {
      columns.map((col, i) => {
        if (activeColumn.index !== undefined && i === activeColumn.index) {
          const width = (e.clientX + tableRef?.current?.parentElement?.scrollLeft || 0) - (tableRef?.current?.parentElement?.getBoundingClientRect()?.left || 164) - activeColumn.ref.parentElement.offsetLeft;

          if (width >= GlobalConstants.DefaultValues.minTableColumnWidth) {
            activeColumn.ref.style.minWidth = `${width}px`;
            activeColumn.ref.style.maxWidth = `${width}px`;
            activeColumn.ref.style.width = `${width}px`;
          }
        }
      });
    }
  };

  const getUpdatedColumns = useCallback(() => {
    return columns.map((col, i) => {
        if (activeColumn.index !== undefined && i === activeColumn.index) {
          return {
            ...col,
            width: activeColumn?.ref?.style?.width ? activeColumn.ref.style.width.replace("px", "") : col.width
          };
        }

        return col;
      }
    );
  }, [activeColumn?.index]);

  const mouseUp = () => {
    removeListeners();
    setColumns(getUpdatedColumns());
    setActiveColumn({ index: undefined });
  };

  const removeListeners = () => {
    window.removeEventListener("mousemove", mouseMove);
    window.removeEventListener("mouseup", mouseUp);
  };

  const mouseDown = (index, elementId, ref = null as any) => {
    if (activeColumn && activeColumn.index === undefined) {
      setActiveColumn({ index, ref: ref?.current || document.getElementById(elementId) });
    }
    else {
      if (activeColumn && activeColumn.index !== undefined) {
        mouseUp();
      }
    }
  };

  useEffect(() => {
    if (activeColumn && activeColumn.index !== undefined) {
      window.addEventListener("mousemove", mouseMove);
      window.addEventListener("mouseup", mouseUp);
    } else {
      removeListeners();
    }

    return () => {
      removeListeners();
    };
  }, [activeColumn.index]);

  const handleContextMenuClick = (e) => {
    e.preventDefault();
    setContextMenuDisplay({ isVisible: true, posX:`${e.pageX - (tableRef?.current?.parentElement?.getBoundingClientRect()?.left || 164 ) + 14}px`, posY: "180px" });
  };

  const handleColumnFilterClick = (e, filterIconRef, options) => {
    e.preventDefault();
    if (columnFilter.prop === options.prop) {
      clearColumnFilter();
    }
    else {
      const rightOffset = ((tableRef?.current?.parentElement?.getBoundingClientRect()?.width || 0) - filterIconRef.current?.parentElement?.getBoundingClientRect()?.x) < 250 ? 250 : 0;
      const { x, y } = filterIconRef.current?.getBoundingClientRect() || { x: 0, y: 0 };
      const positionY = y - 75;
      setColumnFilter({
        isVisible: true,
        posX: `${x - (tableRef?.current?.parentElement?.getBoundingClientRect()?.left || 164) + 30 - rightOffset}px`,
        posY: `${positionY + 32 + window.pageYOffset}px`,
        ...options
      });
    }
  };

  const handleOpenFilterSubmitClick = (e, dropdownOptionsRef) => {
    e.preventDefault();
    const { x, y } = dropdownOptionsRef.current?.getBoundingClientRect() || { x: 0, y: 0 };
    const positionY = (document.body.getBoundingClientRect().height - y - 475) < 0 ? y - 475 : y - 75;
    tableReducerDispatch({
      type: GlobalConstants.LmsTableReducerCases.SET_FILTER_SUBMIT_MODAL,
      payload: {
        isVisible: true,
        posX: `${x - (tableRef?.current?.parentElement?.getBoundingClientRect()?.left || 164) + 20}px`,
        posY: `${positionY + 45}px`
      }
    });
  };

  const getPathParams = (params: string[], rowData) => {
    if (!params || params.length === 0) return undefined;
    const paramsTemp = [] as any[];
    params.map((param) => {
      if (rowData[param]) {
        paramsTemp.push({ [param]: rowData[param] });
      }
    });

    if (paramsTemp.length > 0) {
      return paramsTemp;
    }
  };

  const getClickOptions = (rowData, prop, i) => {
    let itemId = rowData?.id;
    let isClickable = true;
    let isLink = false;
    let params = undefined as any[] | undefined;
    const conditionFunc = props.columnClickOptions?.[prop]?.isClickableConditionFunc;
    if (props.columnClickOptions && (!props.columnClickOptions[prop] || (conditionFunc && !conditionFunc?.(rowData, prop)))) {
      isClickable = false;
    }
    else {
      if (onClickIdFunc) {
        itemId = onClickIdFunc(rowData, prop);
        if (props.onClickPathParams) {
          params = getPathParams(props.onClickPathParams as any[], rowData);
        }
      }
      else if (props.columnClickOptions && props.columnClickOptions[prop]) {
        if (!rowData[props.columnClickOptions[prop].idProp]) {
          isClickable = false;
        }
        else {
          itemId = rowData[props.columnClickOptions[prop].idProp];
          isLink = true;
          params = getPathParams(props.columnClickOptions[prop]?.params as any[], rowData);
        }
      }
      else {
        itemId = filteredData[i] ? filteredData[i][onClickIdProp] || filteredData[i]?.id || filteredData[i]?.Id : "";
        if (props.onClickPathParams) {
          params = getPathParams(props.onClickPathParams as any[], rowData);
        }
      }
    }

    return { itemId, isClickable: !props.skipClick && isClickable, isLink, params };
  };

  const handleIframeLoad = () => {
    const iframeItem = iframeModalRef.current as any;
    if (iframeItem) {
      if (FeatureFlags.IS_DEBUG) {
         console.log(iframeItem?.contentWindow);
      }

      iframeItem?.contentWindow?.sessionStorage?.setItem("iframeTitle", modalIframeSrcPath); // TODO: Updated with needed functionality
    }
  };

  return <BCErrorBoundary>
    <div className="table-page-wrapper">
      <Confirm
        size="tiny"
        open={!!confirmOptions?.isOpen}
        onCancel={() => setConfirmOptions({ isOpen: false })}
        onConfirm={() => {
          if (confirmOptions?.func) {
            confirmOptions?.func();
          }

          setConfirmOptions({ isOpen: false });
        }}
        content={confirmOptions?.message || ""}
        className={confirmOptions?.isAlert ? "form-generator-alert-confirm" : ""}
      />
      <HideColumnsContextMenu
        columnsList={columnsList}
        setColumnsList={setColumnsList}
        setContextMenuDisplay={setContextMenuDisplay}
        tableFilterReducerInstance={tableFilterReducerInstance}
        {...contextMenuDisplay}
      />
      <LmsTableHeaderFilterConfigFields setColumnFilter={setColumnFilter} tableFilterReducerInstance={tableFilterReducerInstance} {...columnFilter} />
      <LmsMessage isInfo={info?.type && info.type !== GlobalConstants.ErrorLevels.ERROR} visible={info?.isVisible} header={info?.header} errorResponse={info?.errorResponse} text={info?.allTableErrors || info?.message} onDismiss={() => setInfo({ ...info, isVisible: false })}/>
      {props.title && <div className="lms-table-title"><span className="lms-table-title-content-container">{props.title}  <span className="table-length">{sortedData?.length || 0}</span></span></div>}
      {(!props.skipFilter && !isDisabled) && (
        <LmsFilterSearchBar
          searchBarLeftComponent={<LMSScreenActionButtons actionButtons={props.actionButtons} useLmsTableInstance={useLmsTableInstance} />}
          filterSubmitModalOptions={{ gridUniqueIdString, refreshSettingsOptions, setSettingOptions }}
          tableFilterReducerInstance={tableFilterReducerInstance}
          handleOpenFilterSubmitClick={handleOpenFilterSubmitClick}
          apiFiltering={props.useApiFiltering}
          settingOptions={settingOptions}
          isSettingsOptionsLoading={isSettingsOptionsLoading}
          settingsOptionsError={settingsOptionsError}
          isLoaderVisible={isLoaderVisible}
        />
      )}
      {isLoaderVisible && (
        <BCLoader
          height={props.loadingHeight ? props.loadingHeight : props.isDisabled ? 50 : 200}
          size={props.isDisabled ? "medium" : "big"}
        />
      )}
      {error && <LmsMessage text={error.message} skipAutoClose/>}
      {displayEmptyState && (
        <LmsMessage text={GlobalConstants.DefaultValues.messageEmptyTableInfoText} skipAutoClose negative={false} isInfo />
      )}
      {isOnClickModalOpen && props?.onClickModalContent && <BCModal
        isModalOpen
        size={props?.onClickModalSize || "large"}
        closeIcon
        onClose={() => setIsOnClickModalOpen(false)}
        content={props?.onClickModalContent(setIsOnClickModalOpen, useLmsTableInstance)}
      />}
      {!!modalIframeSrcPath && <BCModal
        isModalOpen
        size={props?.onClickModalSize || "large"}
        closeIcon
        onClose={() => setModalIframeSrcPath("")}
        content={<iframe ref={iframeModalRef} src={modalIframeSrcPath} width="100%" height="800" style={{border: "none"}} title="Iframe Test" onLoad={handleIframeLoad} />}
      />}
      {displayTable && (
        <div ref={dropContainerRef} className={`lms-table-container lms-overflow-x-scroll ${props.overflowY && "overflow-y-visible"}}`}>
          {(columns && columns.length > 0 && dropContainerRef?.current) && <DndProvider backend={HTML5Backend} options={{ rootElement: dropContainerRef?.current}}>
             <React.Fragment>
              {(!props.skipGrouping && !isDisabled) && <LmsGroupByHeaderList setCurrentViewGroups={setCurrentViewGroups} currentViewGroups={currentViewGroups} activeDragColumn={activeDragColumn}/>}
              <div ref={tableRef} className="table-ref-wrapper">
                <Table
                  className={`lms-table ${columnResizingStyle} ${thin ? " lms-table-thin" : ""}`}
                  celled={props.celled}
                  selectable={!isDisabled && !props.skipClick && selectable}
                  fixed={props.fixed}
                  unstackable={props.unstackable}
                  columns={props.columns}
                  compact
                  fluid="true"
                >
                  <Table.Header>
                    <Table.Row>
                        {currentViewGroups && currentViewGroups.length > 0 && <>
                          {currentViewGroups.map((_, i) => {
                            return <Table.HeaderCell key={`header-indent-cell${i}`} className="lms-table-row-blank-indent-column" />;
                          })}
                        </>}
                        {rowTitle ? <Table.HeaderCell /> : <></>}
                        {(checkbox && !skipCheckbox && !isDisabled) &&
                          <Table.HeaderCell className={`${isBrokerColor ? brokerTableHeaderOverrideCSS : ""} table-header-checkbox`}>{!props.skipCheckboxAll &&
                            <Checkbox onChange={(e, value) => handleCheckboxAll(value)} checked={(checkedItems && filteredData) && checkedItems.length === filteredData.length} />}
                          </Table.HeaderCell>}
                        {columns?.map(({ item, prop, fieldType, width }, i) => {
                          const foundSortable = props.sortable ? props.sortable.find(x => x === item) : undefined;
                          const columnHeaderProps = {
                            item,
                            prop,
                            fieldType,
                            width,
                            isBrokerColor,
                            brokerTableHeaderOverrideCSS,
                            tableProps: props,
                            foundSortable,
                            handleHeaderClick,
                            dataSingleLine,
                            i,
                            myForm,
                            editableFields,
                            sortedHeaders,
                            mouseDown,
                            mouseUp,
                            activeColumn,
                            setActiveDragColumn,
                            activeResizeStyles,
                            setIsEditable,
                            isEditable,
                            handleColumnHeaderDrop,
                            handleContextMenuClick,
                            handleColumnFilterClick,
                            tableFilterReducerInstance
                          };

                          return <LmsTableColumnHeader {...columnHeaderProps} key={`table-header-draggable-cell${i}`} />;
                        })}
                        {props.actions && <Table.HeaderCell className={`${isBrokerColor ? brokerTableHeaderOverrideCSS : ""}`} textAlign='right'>
                          {props.actions.includes("add") && <React.Fragment>
                            <Button
                              className={`${isBrokerColor ? brokerTableHeaderOverrideCSS : ""}`}
                              icon
                              disabled={!props.editMode}
                              onClick={() => props.handleTableAdd()}
                            >
                              <div className="gmr-button-container">
                                <Icon name='add' />
                              </div>
                            </Button>
                          </React.Fragment>}
                        </Table.HeaderCell>}
                        {props.ellipsis && <Table.HeaderCell className={`${isBrokerColor ? brokerTableHeaderOverrideCSS : ""}`} />}
                    </Table.Row>
                  </Table.Header>
                  <Table.Body columns={filteredData && filteredData.length}>
                    {filterPageData?.map((row: { [x: string]: any; statusLookupName: string; isEditHide: boolean; isDeleteHide: any; id: any; } | string | any, i: number) => {
                      const rowIndex = props.useApiFiltering ? i : i + firstEntryIndex;
                      const rowProps = {
                        props,
                        filteredData,
                        setSortedData,
                        checkedItems,
                        handleCheckbox,
                        checkbox,
                        skipCheckbox,
                        isDisabled,
                        currentViewGroups,
                        rowTitle,
                        columns,
                        getClickOptions,
                        handleActionClick,
                        activeColumn,
                        isEditable,
                        selectable,
                        myForm,
                        editableFields,
                        yesNoDropdownOptions,
                        handleFormat,
                        allDropdownOptionsData,
                        allDropdownOptionsLoading,
                        trueFalseDict,
                        handleChange,
                        errors,
                        inputs,
                        getEditableValue,
                        handleDelete,
                        isBrokerColor,
                        isUpward,
                        brokerTableEllipsesOverrideCSS,
                        handlePush
                      };

                      if (row && row.groupedByString) {
                        return <Table.Row key={`table-row-grouped-by${i}`} className="lms-table-group-by-row-container">
                          {currentViewGroups?.map((_, i) => {
                            if (row.nestedIndex > i) {
                              return <Table.Cell className="lms-table-row-blank-indent-column" key={`row-indent-cell${i}`}></Table.Cell>;
                            }
                          })}
                          <Table.Cell
                            key={`grouped-by-string${i}`}
                            className="lms-table-group-by-row">
                            {row.groupedByString}
                          </Table.Cell>
                        </Table.Row>;
                      }
                      else if (row && row.rowData && typeof row.rowData === "object") {
                        return <LmsTableRow rowData={row.rowData} i={row.index} {...rowProps} key={`lms-table-row-${row.index}`} />;
                      }
                      else {
                        return <LmsTableRow rowData={row} i={rowIndex} {...rowProps} key={`lms-table-row-${rowIndex}`} />;
                      }
                    })}
                    {(props.calculatedColumns && filteredData[0]) && <Table.Row className="lms-table-grand-total-row">
                      {checkbox && <Table.Cell className="table-checkbox" />}
                      {Object.keys(filteredData[0]).map((prop, i) => {
                        if (handleHide(prop, props.dataRules, props.hideProps, props.displayProps)) {
                          return (
                            <Table.Cell key={`table-cell-total${i}`} className="grand-total-cell-container">
                              {(prop && props?.calculatedColumns?.includes(prop)) && <React.Fragment>
                                <div className="grand-total-label">Grand Total:</div>
                                <p>{calculateColumn(prop, filteredData)}</p>
                              </React.Fragment>}
                            </Table.Cell>
                          );
                        }
                      })}
                    </Table.Row>}
                  </Table.Body>
                </Table>
              </div>
            </React.Fragment>
          </DndProvider>}
        </div>)}
      {(pagination && displayTable && !isDisabled && !skipPagination && !isLoaderVisible && !displayEmptyState) && columns.length > 0 && <>
        {props.isDefaultPagination && <Pagination
          pointing
          secondary
          activePage={activePage}
          totalPages={totalPages}
          onPageChange={(e, data) => handlePageChange(data.activePage, pagination)}
        />}
        {!props.isDefaultPagination && (!props.isPaginationExpanded || (props.isPaginationExpanded && totalPages >= 8)) && !skipPagination &&
          <div className="lms-table-footer-pagination-manu-container">
            <div className="lms-table-footer-pagination-controls-container">
              <Menu.Item as="span" icon onClick={() => pagination && handlePageChange(1, pagination)}>
                <Icon name={GlobalConstants.Icons.ANGLE_DOUBLE_LEFT as any} />
              </Menu.Item>
              <Menu.Item as="span" icon onClick={() => pagination && handlePageChange(activePage - 1, pagination)}>
                <Icon name={GlobalConstants.Icons.ANGLE_LEFT as any} />
              </Menu.Item>
              <span className="page-label">Page</span>
              <Input size="small" className="pagination-input" onChange={(e, data) => handlePageChange(data.value, pagination)} value={activePage} />
              <span>of {totalPages || 1}</span>
              <Menu.Item as="span" icon onClick={() => pagination && handlePageChange(lastEntryIndex / pagination + 1, pagination)}>
                <Icon name={GlobalConstants.Icons.ANGLE_RIGHT as any} />
              </Menu.Item>
              <Menu.Item as="span" icon onClick={() => pagination && handlePageChange(totalPages, pagination)}>
                <Icon name={GlobalConstants.Icons.ANGLE_DOUBLE_RIGHT as any} />
              </Menu.Item>
              <div className="items-per-page-dropdown-container">
                <Select
                  compact
                  onChange={(e, data) => {
                    setPagination(data?.value as number);
                  }}
                  value={pagination}
                  options={[
                    { key: "5", text: "5", value: 5 },
                    { key: "10", text: "10", value: 10 },
                    { key: "15", text: "15", value: 15 },
                    { key: "50", text: "50", value: 50 },
                    { key: "100", text: "100", value: 100 },
                    { key: "1000", text: "1000", value: 1000 },
                  ]}
                  upward={true}
                  className="items-per-page-dropdown"
                />
                <span>items per page</span>
              </div>
            </div>
            <div className="lms-table-footer-pagination-items-count-container">
              <div className="total-items">{firstEntryIndex + 1} - {lastEntryIndex < totalGridCount ? lastEntryIndex : totalGridCount} of {totalGridCount} items
              </div>
              <span className="refresh-data-icon-container" onClick={() => {
                setActivePage(1);
                refreshTableData();
              }}><Icon name="refresh" /></span>
            </div>
          </div>}
        {props.isPaginationExpanded && totalPages < 8 && <Menu pagination>
          <Menu.Item as='a' icon onClick={() => pagination && handlePageChange(lastEntryIndex / pagination - 1, pagination)}>
            <Icon name='chevron left' />
          </Menu.Item>
          <PaginationButtons />
          <Menu.Item as='a' icon onClick={() => pagination && handlePageChange(lastEntryIndex / pagination + 1, pagination)}>
            <Icon name='chevron right' />
          </Menu.Item>
        </Menu>}
      </>}
    </div>
  </BCErrorBoundary>;
};

export default LmsTable;